import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, zip } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

import { State } from '@app/store/state/state';
import { PackageSeachPayload, PackageSeachResponse } from '@packages/packages-result/entities/packages-search';
import * as actions from '@packages/packages-result/store/actions/packages-result.actions';
import { getUIPackagesResultState, showedPackageDetailsSelector } from '@packages/packages-result/store/selectors/ui.selector';
import { packagesResultRootSelector } from '@packages/packages-result/store/selectors/packages-result-root.selector';
import { HotelResultPackagesPayload } from '@packages/packages-result/entities/hotel-results';
import { AirResultOptionResponse } from '@packages/packages-result/entities/air-results';
import { selectAllHotelResults, hotelResultsWithMarkers } from '@packages/packages-result/store/selectors/hotel-results.selector';
import { HotelSearched } from '@hotels/hotel-results/entities/hotel-searched';
import {
  selectAllFlightResults, selectFlightResultById
} from '@packages/packages-result/store/selectors/flight-results.selector';
import { FlightResultDesktop } from '@app/entities/flight-results';
import { FetchFlightsOptionPayload } from '@flight-results/entities/fetch-flights-option.payload';
import { AirResultPackagesPayload } from '@packages/packages-result/entities/air-results';
import { FilterApplier } from '@shared/flight-itinerary/entities/filters';
import { defaultPackageAirPagination } from '@packages/packages-result/mappers/flight-package-filters.mapper';
import { STEP_FLIGHT_PACKAGE_RESULTS } from '@packages/packages-result/constants/flight-filters.constants';
import { AmenitiesWidget, FlightWidgetsMobile, PriceWidget } from '@app/entities/flight-results-widget';
import { HotelMarker } from '@hotels/hotel-results/entities/hotel-marker';
import { ErrorHandle } from '@shared/no-results/entities/results-errors';
import { GlobalFacade } from '@app/global.facade';
import { mapAirFilterPayload, mapAirMultipleFilterPayload } from '@app/mappers/global-filters/air-global-filters.mapper';
import { amenitiesForWidget, hotelsInfoPrice } from '@app/store/selectors/global-hotel-filters.selector';
import { DEFAULT_HOTEL_PAGINATION, STEP_HOTEL_RESULTS } from '@app/mappers/global-filters/hotel-global-filters.mapper';
import { HotelSearchLocationByPackages } from '@hotels/hotel-results/entities/hotel-search-location';

@Injectable()
export class PackagesResultFacade {

  constructor(
    private readonly store: Store<State>,
    private readonly globalFacade: GlobalFacade,
  ) { }

  public isLoadingResults$: Observable<boolean> = this.globalFacade.ui.results.isLoadingResults$;

  public isLoadingAirOption$: Observable<boolean> = this.store.pipe(
    select(getUIPackagesResultState), map(state => state.isLoadingAirOption));

  public isLoadingMarkers$: Observable<boolean> = this.store.pipe(
    select(getUIPackagesResultState), map(state => state.isLoadingMarkers));

  public isLoadingPackageHotelFilters$: Observable<boolean> = this.store.pipe(
    select(getUIPackagesResultState), map(state => state.isLoadingHotelFilters));

  public isLoadingPackageAirFilters$: Observable<boolean> = this.store.pipe(
    select(getUIPackagesResultState), map(state => state.isLoadingAirFilters));

  public isDisabledInfinityScroll$: Observable<boolean> = this.store.pipe(
    select(getUIPackagesResultState), map(state => state.disabledInfinityScroll));

  public hotelRoomRateId$: Observable<string> = this.store.pipe(
    select(getUIPackagesResultState), map(state => state.hotelRoomRateId));

  public hotelMapView$: Observable<boolean> = this.store.pipe(
    select(getUIPackagesResultState), map(state => state.hotelMapView));

  public airSelected$: Observable<AirResultOptionResponse> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state.airResultSelected));

  public airSelectedDesktop$: Observable<FlightResultDesktop> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state.airResultSelectedDesktop));

  public hotelSelected$: Observable<HotelSearched> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state?.hotelResultSelected));

  public hotelResults$: Observable<HotelSearched[]> = this.store.pipe(select(selectAllHotelResults));

  public hotelsWithMarkers$: Observable<HotelMarker[]> = this.store.pipe(select(hotelResultsWithMarkers));

  public flightResults$: Observable<AirResultOptionResponse[]> = this.store.pipe(
    select(selectAllFlightResults));

  public flightResultsDesktop$: Observable<FlightResultDesktop[]> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state.flightResultsDesktop));

  public packageSearchData$: Observable<PackageSeachResponse> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state?.packageSearchData || {} as PackageSeachResponse));

  public hotelsPayload$: Observable<HotelResultPackagesPayload> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state.hotelsPayload));

  public flightsPayload$: Observable<AirResultPackagesPayload> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state?.flightsPayload));

  public errors$: Observable<ErrorHandle> = this.globalFacade.ui.errorHandle$;

  public errorInPackages$: Observable<boolean> = this.errors$.pipe(map(state => state.status));

  public packageAirWidgets$: Observable<FlightWidgetsMobile> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state.packageAirWidgets));

  public infoAmenitiesWidgets$: Observable<AmenitiesWidget[]> = this.store.pipe(select(amenitiesForWidget));
  
  public infoPriceWidgets$: Observable<PriceWidget[]> = this.store.pipe(select(hotelsInfoPrice));

  public showedPackageDetails$: Observable<string[]> = this.store.pipe(select(showedPackageDetailsSelector));

  public packageHotelDetailSearched$: Observable<HotelSearched> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state.packageHotelDetailSearched));

  public searchPackages(payload: PackageSeachPayload): void {
    this.store.dispatch(actions.searchPackageAction({ payload }));
  }

  public setAirSelected(payload: AirResultOptionResponse): void {
    this.store.dispatch(actions.setAirSelectedAction({ payload }));
  }

  public setAirSelectedDesktop(payload: FlightResultDesktop): void {
    this.store.dispatch(actions.setAirSelectedDesktopAction({ payload }));
  }

  public setHotelSelected(payload: HotelSearched): void {
    this.store.dispatch(actions.setHotelSelectedAction({ payload }));
  }

  public setHotelRoomSelected(id: string): void {
    this.store.dispatch(actions.setHotelRoomSelectedAction({ id }));
  }

  public fetchFlightsOptionForPackage(payload: FetchFlightsOptionPayload): void {
    this.store.dispatch(actions.fetchFlightsOptionForPackageAction({ payload }));
  }

  public flightResultById$(optionId: number): Observable<AirResultOptionResponse> {
    return this.store.pipe(select(selectFlightResultById(optionId)));
  }

  public setHotelMapView(payload: boolean): void {
    this.store.dispatch(actions.setHotelMapView({payload}))
  }

  public searchFlightsWithPagination(isMobile: boolean): void {
    zip(this.packageSearchData$.pipe(take(1)), this.flightsPayload$.pipe(take(1))).pipe(
      filter(([search, flightsPayload]) => search.SearchCountAir > flightsPayload.ResultCountUpperBound),
      map(([search, flightsPayload]) => ({ search, flightsPayload }))
    ).subscribe(({ flightsPayload }) => {
      const payload = {
        ...flightsPayload, ResultCountLowerBound: flightsPayload.ResultCountUpperBound + 1,
        ResultCountUpperBound: flightsPayload.ResultCountUpperBound + STEP_FLIGHT_PACKAGE_RESULTS,
      };
      if (isMobile) this.store.dispatch(actions.searchPackageFlightsForMobileWithPaginationAction({ payload }));
      else this.store.dispatch(actions.searchPackageFlightsForDesktopWithPaginationAction({ payload }));
    }).unsubscribe();
  }

  public searchHotelsWithPagination(): void {
    zip(this.packageSearchData$.pipe(take(1)), this.hotelsPayload$.pipe(take(1))).pipe(
      filter(([search, hotelsPayload]) => search.SearchCountHotel > hotelsPayload.ResultCountUpperBound),
      map(([search, hotelsPayload]) => ({ search, hotelsPayload }))
    ).subscribe(({ hotelsPayload }) => {
      const payload = {
        ...hotelsPayload,
        ResultCountLowerBound:
        hotelsPayload.ResultCountUpperBound + 1,
        ResultCountUpperBound: hotelsPayload.ResultCountUpperBound + STEP_HOTEL_RESULTS,
      };
      this.store.dispatch(actions.searchPackageHotelsWithPaginationAction({ payload }));
    }).unsubscribe();
  }

  public searchHotelsWithDetaultPagination(): void {
    this.clearPackageResults();
    this.hotelsPayload$.pipe(take(1)).pipe(
      tap(hotelsPayload => {
        const payload = {
          ...hotelsPayload,
          IncludeRooms: true,
          ...DEFAULT_HOTEL_PAGINATION
        };
        this.store.dispatch(actions.searchPackageHotelsWithPaginationAction({ payload }));
      })
    ).subscribe().unsubscribe();

  }

  public fetchHotelsMarkers(): void {
    zip(this.packageSearchData$.pipe(take(1)), this.hotelsPayload$.pipe(take(1))).pipe(
      map(([search, hotelsPayload]) => ({ search, hotelsPayload })),
      tap(({ search, hotelsPayload }) => {
        const payload = {
          ...hotelsPayload,
          ResultCountLowerBound: 0,
          ResultCountUpperBound: search.SearchCountHotel,
          IncludeRooms: true
        };
        this.store.dispatch(actions.searchPackageHotelsWithMarkersAction({ payload }));
      })
    ).subscribe().unsubscribe();
  }

  public fetchAirWithFilter(filterApplier: FilterApplier, isMobile: boolean, block: boolean = false): void {
    this.clearPackageResults();
    this.flightsPayload$.pipe(take(1)).subscribe(searchPayload => {
      if (isMobile) {
        this.store.dispatch(actions.searchPackageFlightsForMobileAction({
          payload: mapAirFilterPayload(filterApplier, {
            ...searchPayload, ...defaultPackageAirPagination(isMobile)
          }) as AirResultPackagesPayload,
          fetchFilters: false, block
        }));
      } else {
        this.store.dispatch(actions.searchPackageFlightsForDesktopAction({
          payload: mapAirFilterPayload(filterApplier, {
            ...searchPayload, ...defaultPackageAirPagination(isMobile)
          }) as AirResultPackagesPayload,
          fetchFilters: false, block
        }));
      }
    }).unsubscribe();
  }

  public fetchAirWithMultipleFilter(applied: FilterApplier[], isMobile: boolean, block: boolean | undefined = false): void {
    this.clearPackageResults();
    this.flightsPayload$.pipe(take(1)).subscribe(searchPayload => {
      if (isMobile) {
        this.store.dispatch(actions.searchPackageFlightsForMobileAction({
          payload: mapAirMultipleFilterPayload(applied, {
            ...searchPayload, ...defaultPackageAirPagination(isMobile)
          }) as AirResultPackagesPayload,
          fetchFilters: false, block
        }));
      } else {
        this.store.dispatch(actions.searchPackageFlightsForDesktopAction({
          payload: mapAirMultipleFilterPayload(applied, {
            ...searchPayload, ...defaultPackageAirPagination(isMobile)
          }) as AirResultPackagesPayload,
          fetchFilters: false, block
        }));
      }
    }).unsubscribe();
  }

  public clearPackageResults(): void {
    this.store.dispatch(actions.clearPackageResultsAction());
  }

  public savePackageSearchData(response: PackageSeachResponse): void {
    this.store.dispatch(actions.savePackageSearchDataAction({ response }));
  }

  public disabledInfinityScroll(): void {
    this.store.dispatch(actions.disabledInfinityScrollAction());
  }

  public setShowedPackageDetails(payload: string): void {
    this.store.dispatch(actions.setShowedPackageDetailsAction({ payload }));
  }

  public setPackageResultsUrl(url: string): void {
    this.store.dispatch(actions.setPackageBaseUrlResultAction({ url }));
  }

  public fetchPackageHotelDetailSearched(hotelSearched: HotelSearched): void {
    this.store.dispatch(actions.fetchPackageHotelDetailSearchedAction({ hotel: hotelSearched }));
  }

  public searchLocationOrHotelInfoByCode(payload: HotelSearchLocationByPackages): void {
    this.store.dispatch(actions.searchLocationOrHotelInfoByCodeAction({ payload }));
  }

}
